import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, User } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Global user search dropdown.
 * Appears in the header. Lets any logged-in user (and admin) find another user
 * by name and jump straight to their public profile.
 */
export default function UserSearchBar({ compact = false }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Debounced search
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/users/search?q=${encodeURIComponent(query)}&limit=8`);
        setResults(Array.isArray(r.data) ? r.data : []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Cmd/Ctrl+K to focus
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 30);
      }
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const goto = (id) => {
    setOpen(false);
    setQ("");
    setResults([]);
    nav(`/u/${id}`);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="user-search-wrap">
      {!open ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 30); }}
          data-testid="user-search-open"
          title="Hľadať užívateľa (Ctrl+K)"
          className="inline-flex items-center justify-center w-9 h-9 border border-zinc-700 text-zinc-400 hover:text-gold-400 hover:border-gold-500 transition-colors"
        >
          <Search className="w-4 h-4" />
        </button>
      ) : (
        <div className="flex items-center gap-1 border border-gold-500 bg-zinc-950 pr-1">
          <Search className="w-4 h-4 text-gold-400 ml-2" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={compact ? "Meno…" : "Hľadať užívateľa…"}
            className="bg-transparent border-0 outline-none px-2 py-1.5 text-sm text-white placeholder:text-zinc-600 w-40 sm:w-56"
            data-testid="user-search-input"
          />
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />}
          <button onClick={() => { setOpen(false); setQ(""); }} className="p-1 text-zinc-500 hover:text-red-400" data-testid="user-search-close">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {open && q.trim().length >= 2 && (
        <div className="absolute top-full right-0 mt-1 w-72 max-h-80 overflow-y-auto bg-[var(--wb-bg)] border border-gold-700/50 shadow-2xl z-50" data-testid="user-search-dropdown">
          {results.length === 0 && !loading && (
            <div className="px-3 py-4 text-xs text-zinc-500 font-mono text-center">Žiadny užívateľ.</div>
          )}
          {results.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => goto(u.id)}
              data-testid={`user-search-result-${u.id}`}
              className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-zinc-900 border-b border-zinc-800/50 last:border-0"
            >
              <div className="w-7 h-7 bg-zinc-800 border border-zinc-700 overflow-hidden flex-shrink-0">
                {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-[10px] font-display font-bold text-zinc-400 uppercase">{u.name?.[0]}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-display font-bold text-white truncate inline-flex items-center gap-1.5">
                  {u.name}
                  {u.certified && <span className="text-[8px] font-mono uppercase tracking-wider text-yellow-400">✓ CERT</span>}
                  {u.plan_tier === "gold" && <span className="text-[8px] font-mono uppercase tracking-wider text-amber-400">GOLD</span>}
                  {u.role === "admin" && <span className="text-[8px] font-mono uppercase tracking-wider text-red-400">ADMIN</span>}
                </div>
                {u.certificate_no && <div className="text-[10px] font-mono text-zinc-500">{u.certificate_no}</div>}
              </div>
              <User className="w-3 h-3 text-zinc-600" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
